// The Customer -> Site -> Asset creation chain shared by every "raise something against a lift
// that is not on the register yet" flow (Session AEP's call-out intake; Session AOI's condition
// report / tech visit intake, RaiseWorkModal.tsx). RESUMES from whatever a previous attempt
// already committed, so a retry after a mid-chain failure never creates a second customer or
// site: the office presses the button again, and each step that already landed is reused rather
// than repeated.
//
// Extracted from CalloutsPage.tsx's createNewCustomerChain (Session AEP) rather than left
// duplicated, because the resumable retry-safety is the one thing that must not drift between its
// call sites. CalloutsPage.tsx's own behaviour is UNCHANGED by the extraction: it still never
// passes customerType, so its new call-out customer still takes the schema default (Customer.
// customerType @default(CONTRACT)) exactly as before. A caller that wants the AFM one-off /
// NON_CONTRACT classification (RaiseWorkModal.tsx's own createOneOffCustomer already does this for
// the planned-repair no-lift path) passes customerType explicitly; CalloutsPage.tsx does not.
import { useRef, useState } from "react";
import { customers as customersResource, sites as sitesResource, assets as assetsResource } from "./resources";
import { friendlyApiError } from "./friendlyApiError";
import { erasedPersonRefusal } from "./erasedPerson";

// Which of the three records the chain got to before it failed. The office is told exactly this,
// with a link to each thing that DID land, so a half-finished chain is never a mystery and never
// has to be guessed at from the registers.
export type ChainStep = "customer" | "site" | "lift";
export const CHAIN_STEP_LABEL: Record<ChainStep, string> = { customer: "customer", site: "site", lift: "lift" };

export interface NewCustomerChainInput {
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  // Omitted -> the schema default (CONTRACT). Pass "NON_CONTRACT" for a caller with no
  // maintenance contract at creation.
  customerType?: "CONTRACT" | "NON_CONTRACT";
  siteName: string;
  siteAddress: string;
  eleNumber?: string;
  liftType?: string;
}

export interface CreatedChainState {
  customer?: { id: string; name: string };
  site?: { id: string; name: string };
  asset?: { id: string };
}

export function useNewCustomerChain() {
  // What the chain has ALREADY committed. This is the thing that makes a retry safe: a second
  // attempt resumes from here instead of creating a second customer, and every record that landed
  // is shown with a link (by the caller's own JSX) so nothing is ever stranded unfindable.
  const [created, setCreated] = useState<CreatedChainState>({});
  const [chainFailure, setChainFailure] = useState<{ step: ChainStep; message: string } | null>(null);
  // Session ASQ, Item 3's review, F5: the customer step was refused because its email or phone belongs to a person whose
  // data was erased (409 erased_person). Kept apart from chainFailure, because "press the button again" would only be
  // refused again: the callers show this message with ONE explicit "Create anyway", which calls allowErasedPerson() and
  // runs their own submit once more. A ref, not state, so the very next run reads it without waiting for a render.
  const [chainErased, setChainErased] = useState<string | null>(null);
  const goAheadErased = useRef(false);

  // Create the Customer, then the Site, then the lift, RESUMING from anything a previous attempt
  // already committed. Returns the new lift's id, or null when a step failed (in which case
  // chainFailure + created describe exactly where it stopped). Never throws: every failure is
  // caught here and turned into chainFailure, so a caller never needs its own try/catch around it.
  async function run(input: NewCustomerChainInput): Promise<string | null> {
    let customer = created.customer;
    let site = created.site;
    let asset = created.asset;
    let step: ChainStep = "customer";
    try {
      if (!customer) {
        const email = (input.customerEmail ?? "").trim();
        const phone = (input.customerPhone ?? "").trim();
        const c = await customersResource.create({
          name: input.customerName.trim(),
          ...(input.customerType ? { customerType: input.customerType } : {}),
          ...(email ? { billingEmail: email } : {}),
          ...(phone ? { phone } : {}),
          ...(goAheadErased.current ? { createAnyway: true } : {}),
        });
        customer = { id: c.id, name: c.name };
        goAheadErased.current = false;
        setChainErased(null);
        setCreated((p) => ({ ...p, customer }));
      }
      step = "site";
      if (!site) {
        const s = await sitesResource.create({
          customerId: customer.id,
          name: input.siteName.trim(),
          address: input.siteAddress.trim(),
          // A site work is about to happen at is a live site, not a sales prospect.
          status: "active",
        });
        site = { id: s.id, name: s.name };
        setCreated((p) => ({ ...p, site }));
      }
      step = "lift";
      if (!asset) {
        const ele = (input.eleNumber ?? "").trim();
        const a = await assetsResource.create({
          siteId: site.id,
          ...(ele ? { eleNumber: ele } : {}),
          ...(input.liftType ? { liftType: input.liftType } : {}),
        });
        asset = { id: a.id };
        setCreated((p) => ({ ...p, asset }));
      }
      setChainFailure(null);
      return asset.id;
    } catch (err) {
      goAheadErased.current = false;
      const erased = step === "customer" ? erasedPersonRefusal(err) : null;
      if (erased) {
        setChainFailure(null);
        setChainErased(erased.message);
        return null;
      }
      setChainErased(null);
      setChainFailure({
        step,
        message: friendlyApiError(err, `Could not create the ${CHAIN_STEP_LABEL[step]}. Please try again.`),
      });
      return null;
    }
  }

  /** The office chose "Create anyway": the next run creates the customer with createAnyway (the server audits it). */
  function allowErasedPerson() {
    goAheadErased.current = true;
  }

  function reset() {
    setCreated({});
    setChainFailure(null);
    setChainErased(null);
    goAheadErased.current = false;
  }

  return { created, chainFailure, chainErased, run, reset, allowErasedPerson };
}
